import { Component, Input, computed, signal } from '@angular/core';

/**
 * Small circular avatar that renders a person's initials with a deterministic
 * background color derived from their name. Used where no profile image is
 * available (e.g. notification actors).
 */
@Component({
  selector: 'app-initials-avatar',
  standalone: true,
  template: `
    <span
      class="ia"
      [style.width.px]="size"
      [style.height.px]="size"
      [style.fontSize.px]="size * 0.42"
      [style.background]="bgColor()"
      [attr.title]="name"
    >{{ initials() }}</span>
  `,
  styles: [
    `
      :host {
        display: inline-flex; /* size the host to the circle; no inline baseline gap */
        line-height: 0;
      }

      .ia {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 9999px;
        color: #ffffff;
        font-family: var(--dk-font-sans);
        font-weight: 600;
        line-height: 1;
        flex-shrink: 0;
        user-select: none;
        box-sizing: border-box;
        /* Optical centering: all-caps initials sit a hair high; nudge down (scales with font-size). */
        padding-top: 0.1em;
      }
    `,
  ],
})
export class InitialsAvatarComponent {
  private readonly _name = signal('');

  @Input({ required: true })
  set name(value: string) {
    this._name.set(value ?? '');
  }
  get name(): string {
    return this._name();
  }

  @Input() size = 18;

  private readonly palette = ['#7C5CFC', '#34C48B', '#F59E0B', '#3B82F6', '#EC4899', '#10B981'];

  readonly initials = computed(() => {
    const parts = this._name().trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  });

  readonly bgColor = computed(() => {
    const name = this._name();
    if (!name) return this.palette[0];
    const hash = name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return this.palette[hash % this.palette.length];
  });
}
